import type { JSONTypes } from "common/types/common-types";
import type { Result } from "common/utils/result";

export type ResponseBody = Blob | JSONTypes;

export type ReadResponseMethods = "json" | "text" | "blob";

export type LogoutFn = () => void | Promise<Result<null>>;

export type GetMessageFromErrorResponseFn<TErrorBody extends ResponseBody> = (
  responseBody: TErrorBody,
) => string;

export interface RestOptions<TErrorBody extends ResponseBody>
  extends Omit<RequestInit, "body"> {
  body?: RequestInit["body"] | JSONTypes;
  baseURL?: string;
  onUnauthorized?: LogoutFn;
  getMessageFromErrorResponse?: GetMessageFromErrorResponseFn<TErrorBody>;
}

export type HeadersObject = Readonly<{ [headerName: string]: string }>;

export interface ResponseContent {
  status: number;
  headers: HeadersObject;
}

export interface FetchError {
  kind: "fetchError";
  message: string;
}

export interface ErrorRequest extends Omit<RequestInit, "signal"> {
  url: string;
  headers?: HeadersObject;
}

export interface RestErrorObject {
  message: string;
  response: ResponseContent;
  request: ErrorRequest;
}

export interface EmptyRestError extends RestErrorObject {
  kind: "emptyRestError";
}

export interface RestErrorWithBody<TErrorBody extends ResponseBody>
  extends RestErrorObject {
  kind: "restErrorWithBody";
  response: ResponseContent & { body: TErrorBody };
}

export type RestError<TErrorBody extends ResponseBody> =
  | EmptyRestError
  | RestErrorWithBody<TErrorBody>;

export type ErrorObject<TErrorBody extends ResponseBody> =
  | FetchError
  | RestError<TErrorBody>;

export type RestErrorBuilder<TErrorBody extends ResponseBody> = (
  message: string,
  body?: TErrorBody,
) => string;

export interface SuccessfulEmptyResponse extends ResponseContent {
  kind: "empty";
}

export interface SuccessfulResponseWithBody<TResponseBody extends ResponseBody>
  extends ResponseContent {
  kind: "withBody";
  body: TResponseBody;
}

export type SuccessfulResponse<TResponseBody extends ResponseBody> =
  | SuccessfulEmptyResponse
  | SuccessfulResponseWithBody<TResponseBody>;

function getFetchInput(endpoint: string, baseURL: string) {
  if (endpoint.startsWith("https://")) {
    return endpoint;
  }
  return `${baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL}/${
    endpoint.startsWith("/") ? endpoint.slice(1) : endpoint
  }`;
}

function isRequestInitBody<TErrorBody extends ResponseBody>(
  body: RestOptions<TErrorBody>["body"],
): body is RequestInit["body"] {
  return (
    body === undefined ||
    body === null ||
    typeof body === "string" ||
    body instanceof ReadableStream ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    ArrayBuffer.isView(body) ||
    body instanceof FormData ||
    body instanceof URLSearchParams
  );
}

function getInitBody<TErrorBody extends ResponseBody>(
  body: RestOptions<TErrorBody>["body"],
): {
  body?: RequestInit["body"];
} {
  if (body === undefined) {
    return {};
  }

  return {
    body: isRequestInitBody<TErrorBody>(body) ? body : JSON.stringify(body),
  };
}

function getFetchInit<TErrorBody extends ResponseBody>(
  body: RestOptions<TErrorBody>["body"],
  customConfig: Omit<RequestInit, "body">,
): RequestInit {
  return {
    method: body ? "post" : "get",
    ...customConfig,
    headers: {
      ...(isRequestInitBody<TErrorBody>(body)
        ? {}
        : { "content-type": "application/json" }),
      ...customConfig.headers,
    },
    ...getInitBody<TErrorBody>(body),
  };
}

function getHeadersObject(headers: Headers): HeadersObject {
  return [...headers.entries()].reduce(
    (accumulator, [key, value]) => ({ ...accumulator, [key]: value }),
    {},
  );
}

function getErrorBodyMessage<TErrorBody extends ResponseBody>(
  getMessageFromErrorResponse: GetMessageFromErrorResponseFn<TErrorBody>,
  body: TErrorBody,
) {
  try {
    const message = getMessageFromErrorResponse(body);
    if (typeof message === "string") {
      return message;
    }
    return "The error message is not a string. Check the provided `getMessageFromErrorResponse` function.";
  } catch (error) {
    return `Error running \`getMessageFromErrorResponse\`${
      error instanceof Error ? `: \`${error.message}\`` : ""
    }`;
  }
}

function getRestErrorBuilder<TErrorBody extends ResponseBody>(
  url: string,
  request: RequestInit,
  response: Response,
  getMessageFromErrorResponse?: GetMessageFromErrorResponseFn<TErrorBody>,
): RestErrorBuilder<TErrorBody> {
  function omit(object: RequestInit | HeadersInit, keys: string[]) {
    return Object.entries(object)
      .filter(([key]) => !keys.includes(key.toLowerCase()))
      .reduce(
        (accumulator, [key, value]) => ({ ...accumulator, [key]: value }),
        {},
      );
  }

  return (...[message, body]: Parameters<RestErrorBuilder<TErrorBody>>) => {
    if (getMessageFromErrorResponse !== undefined) {
      return body === undefined
        ? message
        : getErrorBodyMessage<TErrorBody>(getMessageFromErrorResponse, body);
    }

    const errorObject = {
      message,
      response: {
        status: response.status,
        headers: getHeadersObject(response.headers),
      },
      request: {
        url,
        ...omit(request, ["signal"]),
        ...(request.headers === undefined
          ? {}
          : { headers: omit(request.headers, ["authorization"]) }),
      },
    };

    if (body === undefined) {
      const emptyRestError: EmptyRestError = {
        ...errorObject,
        kind: "emptyRestError",
      };

      return JSON.stringify(emptyRestError);
    }

    const restErrorWithBody: RestErrorWithBody<TErrorBody> = {
      ...errorObject,
      kind: "restErrorWithBody",
      response: {
        ...errorObject.response,
        body,
      },
    };

    return JSON.stringify(restErrorWithBody);
  };
}

async function readResponseBody<
  TResponseBody extends ResponseBody,
  TErrorBody extends ResponseBody,
>(
  restErrorBuilder: RestErrorBuilder<TErrorBody>,
  response: Response,
  readResponseMethod: ReadResponseMethods,
): Promise<Result<TResponseBody>> {
  try {
    return await response.clone()[readResponseMethod]();
  } catch (error) {
    return new Error(
      restErrorBuilder(
        `Error reading the response body${
          error instanceof Error ? `: ${error.message}` : ""
        }`,
      ),
    );
  }
}

async function isEmptyResponse<TErrorBody extends ResponseBody>(
  restErrorBuilder: RestErrorBuilder<TErrorBody>,
  method: RequestInit["method"],
  response: Response,
): Promise<Result<boolean>> {
  if (
    method?.toLowerCase() === "head" ||
    response.status === 204 ||
    response.status === 205 ||
    response.headers.get("content-length") === "0"
  ) {
    return true;
  }

  const responseAsTextResult = await readResponseBody<string, TErrorBody>(
    restErrorBuilder,
    response,
    "text",
  );
  if (responseAsTextResult instanceof Error) {
    return responseAsTextResult;
  }

  return responseAsTextResult.length === 0;
}

function getResponseBody<
  TResponseBody extends ResponseBody,
  TErrorBody extends ResponseBody,
>(restErrorBuilder: RestErrorBuilder<TErrorBody>, response: Response) {
  const contentType = response.headers.get("content-type");
  if (contentType?.includes("json")) {
    return readResponseBody<TResponseBody, TErrorBody>(
      restErrorBuilder,
      response,
      "json",
    );
  }
  if (contentType?.startsWith("text/")) {
    return readResponseBody<TResponseBody, TErrorBody>(
      restErrorBuilder,
      response,
      "text",
    );
  }

  return readResponseBody<TResponseBody, TErrorBody>(
    restErrorBuilder,
    response,
    "blob",
  );
}

async function handleUnauthorizedResponse<TErrorBody extends ResponseBody>(
  restErrorBuilder: RestErrorBuilder<TErrorBody>,
  onUnauthorized?: LogoutFn,
  body?: TErrorBody,
) {
  const logoutResult = await onUnauthorized?.();
  if (logoutResult instanceof Error) {
    return new Error(
      restErrorBuilder(`Logout error: ${logoutResult.message}`, body),
    );
  }
  return new Error(restErrorBuilder("Unauthorized", body));
}

async function handleNotSuccessfulResponse<TErrorBody extends ResponseBody>(
  restErrorBuilder: RestErrorBuilder<TErrorBody>,
  method: RequestInit["method"],
  response: Response,
  onUnauthorized?: LogoutFn,
) {
  const isEmptyResponseResult = await isEmptyResponse<TErrorBody>(
    restErrorBuilder,
    method,
    response,
  );
  if (isEmptyResponseResult instanceof Error) {
    return isEmptyResponseResult;
  }

  if (isEmptyResponseResult) {
    if (response.status === 401) {
      return handleUnauthorizedResponse<TErrorBody>(
        restErrorBuilder,
        onUnauthorized,
      );
    }

    return new Error(
      restErrorBuilder("Received not successful empty response"),
    );
  }

  const bodyResult = await getResponseBody<TErrorBody, TErrorBody>(
    restErrorBuilder,
    response,
  );
  if (bodyResult instanceof Error) {
    return bodyResult;
  }

  if (response.status === 401) {
    return handleUnauthorizedResponse<TErrorBody>(
      restErrorBuilder,
      onUnauthorized,
      bodyResult,
    );
  }

  return new Error(
    restErrorBuilder("Received not successful response", bodyResult),
  );
}

async function handleSuccessfulResponse<
  TResponseBody extends ResponseBody,
  TErrorBody extends ResponseBody,
>(
  restErrorBuilder: RestErrorBuilder<TErrorBody>,
  method: RequestInit["method"],
  response: Response,
): Promise<Result<SuccessfulResponse<TResponseBody>>> {
  const isEmptyResponseResult = await isEmptyResponse<TErrorBody>(
    restErrorBuilder,
    method,
    response,
  );
  if (isEmptyResponseResult instanceof Error) {
    return isEmptyResponseResult;
  }

  if (isEmptyResponseResult) {
    const emptyResponse: SuccessfulEmptyResponse = {
      kind: "empty",
      status: response.status,
      headers: getHeadersObject(response.headers),
    };
    return Object.freeze(emptyResponse);
  }

  const bodyResult = await getResponseBody<TResponseBody, TErrorBody>(
    restErrorBuilder,
    response,
  );
  if (bodyResult instanceof Error) {
    return bodyResult;
  }

  const responseWithBody: SuccessfulResponseWithBody<TResponseBody> = {
    kind: "withBody",
    status: response.status,
    headers: getHeadersObject(response.headers),
    body: bodyResult,
  };
  return Object.freeze(responseWithBody);
}

export type DefaultErrorBody = {
  message: string;
};

export function getErrorObject<
  TErrorBody extends ResponseBody = DefaultErrorBody,
>(errorMessage: string): Result<ErrorObject<TErrorBody>> {
  try {
    const object = JSON.parse(errorMessage);
    return Object.freeze(object);
  } catch (error) {
    return new Error(
      `Parse JSON error${
        error instanceof Error ? `: \`${error.message}\`` : ""
      }`,
    );
  }
}

export async function rest<
  TResponseBody extends ResponseBody,
  TErrorBody extends ResponseBody = DefaultErrorBody,
>(
  endpoint: string,
  {
    baseURL = globalThis.location.origin,
    onUnauthorized,
    getMessageFromErrorResponse,
    body,
    ...customConfig
  }: RestOptions<TErrorBody> = {},
) {
  const input = getFetchInput(endpoint, baseURL);
  const init = getFetchInit<TErrorBody>(body, customConfig);

  try {
    return await fetch(input, init).then((response) => {
      const restErrorBuilder = getRestErrorBuilder<TErrorBody>(
        input,
        init,
        response,
        getMessageFromErrorResponse,
      );

      if (response.ok) {
        return handleSuccessfulResponse<TResponseBody, TErrorBody>(
          restErrorBuilder,
          init.method,
          response,
        );
      }

      return handleNotSuccessfulResponse<TErrorBody>(
        restErrorBuilder,
        init.method,
        response,
        onUnauthorized,
      );
    });
  } catch (error) {
    const message = `Error running \`fetch\`${
      error instanceof Error ? `: \`${error.message}\`` : ""
    }`;

    if (getMessageFromErrorResponse !== undefined) {
      return new Error(message);
    }

    const errorObject: FetchError = {
      kind: "fetchError",
      message,
    };

    return new Error(JSON.stringify(errorObject));
  }
}
